import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

// API response types
interface LeaderboardEntry {
  id: string;
  boardId: string;
  venueId: string;
  playerName: string;
  score: number;
  metadata: Record<string, unknown> | null;
  periodKey: string | null;
  rank: number;
  createdAt: string;
  updatedAt: string;
}

interface EntriesResponse {
  success: boolean;
  data: {
    items: LeaderboardEntry[];
    pagination: { total: number; page: number; limit: number };
  };
}

// Map API entry → StaffMember shape
export interface StaffMemberMapped {
  id: string;
  name: string;
  avatar: string;
  membershipsSold: number;
  valueSold: number;
  previousRank: number;
  rank: number;
  movement: number;
}

function mapEntryToStaffMember(entry: LeaderboardEntry): StaffMemberMapped {
  return {
    id: entry.id,
    name: entry.playerName,
    avatar: `/avatars/default.jpg`,
    membershipsSold: entry.score,
    valueSold: typeof entry.metadata?.value === "number" ? entry.metadata.value : 0,
    previousRank: entry.rank,
    rank: entry.rank,
    movement: 0,
  };
}

export function useEntries(venueId: string | null | undefined, boardId: string | null | undefined) {
  return useQuery({
    queryKey: ["entries", venueId, boardId],
    queryFn: async () => {
      if (!venueId || !boardId) return [];
      const response = await apiGet<EntriesResponse>(
        `/venues/${venueId}/leaderboards/${boardId}/entries`
      );
      return response.data.items.map(mapEntryToStaffMember);
    },
    enabled: !!venueId && !!boardId,
    refetchInterval: 30000,
  });
}
